import { useEffect, useState, useMemo } from "react";
import { Form, Link, useLoaderData, useActionData, useNavigation } from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton } from "../../components/common";
import { MEMBERSHIP_TYPES } from "../../schemas";
import { generateSlug } from "../../utils";
import type { CreateNetworkLoaderData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import type { MembershipType } from "../../types/api";
import styles from "./CreateNetworkPage.module.css";

export default function CreateNetworkPage() {
  const { organizationId } = useLoaderData() as CreateNetworkLoaderData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();

  const isSubmitting = navigation.state === "submitting";

  const [name, setName] = useState("");
  const [slugOverride, setSlugOverride] = useState("");
  const [showSlugInput, setShowSlugInput] = useState(false);
  const [membershipType, setMembershipType] = useState<MembershipType>("invite_only");

  const generatedSlug = useMemo(() => generateSlug(name), [name]);
  const displaySlug = slugOverride || generatedSlug;

  useEffect(() => {
    if (actionData?.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.createNetworkContainer}>
          <div className={styles.backSection}>
            <Link to={`/organizations/${organizationId}/networks`} className={styles.backLink}>
              &larr; Back to Networks
            </Link>
          </div>

          <div className={styles.card}>
            <h1>Create Network</h1>
            <p className={styles.description}>
              Set up a new podcast network. You can adjust all settings after creation.
            </p>

            <Form method="post" className={styles.form}>
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.name ? styles.error : ""}`}
              >
                <label htmlFor="name">Network Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Podcast Network"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
                {actionData?.fieldErrors?.name && (
                  <span className={styles.formError}>{actionData.fieldErrors.name}</span>
                )}
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.slug ? styles.error : ""}`}
              >
                <label htmlFor="slug">
                  URL Slug
                  {!showSlugInput && displaySlug && (
                    <button
                      type="button"
                      className={styles.editSlugButton}
                      onClick={() => setShowSlugInput(true)}
                    >
                      Edit
                    </button>
                  )}
                </label>
                {showSlugInput ? (
                  <input
                    id="slug"
                    name="slug"
                    type="text"
                    placeholder="podcast-network"
                    value={slugOverride}
                    onChange={(e) => setSlugOverride(e.target.value.toLowerCase())}
                    disabled={isSubmitting}
                  />
                ) : (
                  <>
                    <div className={styles.slugPreview}>
                      {displaySlug || (
                        <span className={styles.slugPlaceholder}>auto-generated-from-name</span>
                      )}
                    </div>
                    <input type="hidden" name="slug" value={slugOverride} />
                  </>
                )}
                {actionData?.fieldErrors?.slug && (
                  <span className={styles.formError}>{actionData.fieldErrors.slug}</span>
                )}
                <span className={styles.hint}>Used to identify your network in URLs</span>
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.description ? styles.error : ""}`}
              >
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  placeholder="Describe what your network is about..."
                  rows={4}
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.description && (
                  <span className={styles.formError}>{actionData.fieldErrors.description}</span>
                )}
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.logoUrl ? styles.error : ""}`}
              >
                <label htmlFor="logoUrl">Logo URL</label>
                <input
                  id="logoUrl"
                  name="logoUrl"
                  type="url"
                  placeholder="https://example.com/logo.png"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.logoUrl && (
                  <span className={styles.formError}>{actionData.fieldErrors.logoUrl}</span>
                )}
                <span className={styles.hint}>Square image recommended</span>
              </div>

              <div className={styles.formGroup}>
                <span className={styles.fieldLabel}>Membership Type</span>
                <div className={styles.membershipOptions}>
                  {MEMBERSHIP_TYPES.map((option) => (
                    <label
                      key={option.value}
                      aria-label={option.label}
                      className={`${styles.membershipOption} ${membershipType === option.value ? styles.selected : ""}`}
                    >
                      <input
                        type="radio"
                        name="membershipType"
                        value={option.value}
                        checked={membershipType === option.value}
                        onChange={() => setMembershipType(option.value)}
                        disabled={isSubmitting}
                      />
                      <div className={styles.membershipOptionText}>
                        <span className={styles.membershipOptionLabel}>{option.label}</span>
                        <span className={styles.membershipOptionDesc}>{option.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.formActions}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingText="Creating..."
                >
                  Create Network
                </Button>
                <LinkButton to={`/organizations/${organizationId}/networks`} variant="secondary">
                  Cancel
                </LinkButton>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
